import { berlinUhrzeit } from "@dj/domain/gemeinsam";
import { abstandUndRichtung, entfernungText, naechstgelegen } from "@dj/domain/lage";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Textfeld } from "../design/Formular";
import { Schutzflaeche } from "../design/Hinweis";
import { Kachel } from "../design/Kachel";
import { useRueckgaengig } from "../design/Rueckgaengig";
import { useSperre } from "../design/sperre";
import { Symbol } from "../design/Symbol";
import type { Zeile } from "../plattform/lesemodelle";
import { positionMessen, type Messung } from "../plattform/position";
import { serverkontakt } from "../plattform/serverzeit";
import { personAus, positionText, useDrueckjagdSammlung, useMeinEintrag, useMeineAufgaben, type Rahmen } from "./daten";
import { rettungspunkteAus } from "./eingehend";
import { mitGenauigkeit, mitPosition } from "./meldungen";

/**
 * Abläufe „Hund vermisst“, „Hund zurück“ und „Hund gesichtet“ (oberflaeche.md §5, F-174, F-175; N-09: zwei Fingertipps):
 * Kachel auf der Startansicht → Hund. Die Meldung steht danach 10 Sekunden im Rückgängig-Fenster auf der Startansicht
 * (ADR-0023) und geht dann in die Warteschlange (ADR-0020). „Hund vermisst“ und „Hund gesichtet“ tragen die Position
 * des Geräts (letzte bekannte Position bzw. Ort der Sichtung, LIM-0009); bei der Sichtung optional eine Bemerkung.
 *
 * - „Hund vermisst“: die eigenen Hunde im Treiben, die nicht schon vermisst sind;
 * - „Hund zurück“: die eigenen vermissten Hunde, für den Jagdleiter alle vermissten Hunde;
 * - „Hund gesichtet“: alle vermissten Hunde.
 */
export type Hundemeldung = "Hund vermisst" | "Hund zurück" | "Hund gesichtet";

const SYMBOL: Record<Hundemeldung, string> = { "Hund vermisst": "hund-vermisst", "Hund zurück": "hund-zurueck", "Hund gesichtet": "hund-gesichtet" };

export function HundAblauf({ rahmen, meldung }: { rahmen: Rahmen; meldung: Hundemeldung }) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const rg = useRueckgaengig();
  const { rollen } = useMeinEintrag(id);
  const aufgaben = useMeineAufgaben(id);
  const hunde = useDrueckjagdSammlung(id, "hunde_im_treiben");
  const vermisst = useDrueckjagdSammlung(id, "vermisste_hunde");
  const [bemerkung, setBemerkung] = useState("");
  const rettungspunkte = useDrueckjagdSammlung(id, "rettungspunkte");
  const messung = useRef<Promise<Messung | null> | null>(null);
  const [gemessen, setGemessen] = useState<Messung | null>(null);
  const gesperrt = useSperre("ablauf", meldung);
  useEffect(() => {
    if (meldung === "Hund zurück") return;
    let aktiv = true;
    messung.current = positionMessen();
    void messung.current.then((m) => { if (aktiv) setGemessen(m); });
    return () => { aktiv = false; };
  }, [meldung]);

  const ich = aufgaben.person?.Kennung ?? null;
  const offen = vermisst.filter((v) => v["Vermisststatus"] !== "zurück");
  const vonMir = (d: Zeile, feld: string) => !!ich && personAus(d[feld])?.Kennung === ich;
  const nachName = (a: Zeile, b: Zeile) => String(a["Hund"]).localeCompare(String(b["Hund"]), "de");
  const auswahl: Array<{ name: string; wahl: Zeile }> = (meldung === "Hund vermisst"
    ? hunde.filter((h) => vonMir(h, "Führer") && !offen.some((v) => v["Hund"] === h["Hund"] && vonMir(v, "Hundeführer")))
    : meldung === "Hund zurück"
      ? offen.filter((v) => vonMir(v, "Hundeführer") || rollen.includes("Jagdleiter"))
      : offen).sort(nachName).map((d) => ({ name: String(d["Hund"]), wahl: d }));

  const zurStartansicht = () => navigate(`/drueckjagd/${id}`, { replace: true });

  const melden = (name: string, d: Zeile) => {
    const v = {
      begriff: meldung, symbol: SYMBOL[meldung], angaben: [["hund", name]] as const, titel: () => `${meldung} ${name}`,
      ...(meldung === "Hund vermisst"
        ? { befehl: "HundVermisstMelden", daten: { "Drückjagd": id, Hund: d._id } }
        : meldung === "Hund zurück"
          ? { befehl: "HundZurueckMelden", daten: { Vermisstmeldung: d._id } }
          : { befehl: "HundGesichtetMelden", daten: { Vermisstmeldung: d._id, ...(bemerkung.trim() ? { Bemerkung: bemerkung.trim() } : {}) } }),
    };
    if (meldung === "Hund zurück") rg.vormerken(v);
    else mitPosition(rg, v, mitGenauigkeit, messung.current ?? positionMessen());
    zurStartansicht();
  };

  // Schutzfläche über den Hundekacheln (design/README §14, Referenz `#s-hund-vermisst`, `#s-hund-gesichtet`): Meldung
  // und Uhrzeit, eigene Position, nächster Rettungspunkt; kein Hundename, der neben der gleichnamigen Kachel stünde
  const jetzt = berlinUhrzeit(new Date(Date.now() + serverkontakt().abweichung));
  const punkt = gemessen ? naechstgelegen(gemessen.position, rettungspunkteAus(rettungspunkte)) : null;
  const rettungspunkt = punkt && gemessen
    ? <>Rettungspunkt <span className="kennung">{punkt.Kennung}</span> · {entfernungText(abstandUndRichtung(gemessen.position, punkt).meter)}</>
    : aufgaben.naechsterRettungspunkt ? <>Rettungspunkt <span className="kennung">{aufgaben.naechsterRettungspunkt.Kennung}</span></> : null;
  const schutz = [
    <><b>{meldung}</b> · {jetzt}</>,
    <>eigene Position · <span className="kennung">{positionText(gemessen?.position)}</span></>,
    ...(rettungspunkt ? [rettungspunkt] : []),
  ];

  return rahmen({
    drueckjagdId: id, name: meldung, ablauf: true,
    titel: meldung, schritte: `${meldung} · 1 von 1`, sperre: gesperrt,
    children: (
      <>
        <Kachel symbol="zurueck" begriff="Zurück" art="kompakt" className="zurueck-oben" onPress={zurStartansicht} />
        <div className="r12">
          <div className="hinweis platte" data-zeile-fest="">
            <Symbol name={SYMBOL[meldung]} />
            <div><b>{meldung}</b><span>{aufgaben.person?.Name ?? ""}</span></div>
          </div>
          {meldung === "Hund zurück" ? null : <Schutzflaeche gross zeilen={schutz} />}
          {meldung === "Hund gesichtet" ? <Textfeld label="Bemerkung" wert={bemerkung} onChange={setBemerkung} /> : null}
        </div>
        <div className="r3">
          <div className={`raster raster--${Math.min(Math.max(auswahl.length, 1), 3)}`} data-gleich="">
            {auswahl.map((a) => <Kachel key={a.wahl._id} symbol="hund" begriff={a.name} art="wahl" onPress={() => melden(a.name, a.wahl)} />)}
          </div>
          {auswahl.length === 0 ? <div className="leer"><Symbol name="hund" /><span>Keine Hunde</span></div> : null}
        </div>
      </>
    ),
  });
}
